import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import SearchBox from '../components/SearchBox';
import SelectBox from '../components/SelectBox';
import Button from 'react-bootstrap/Button';
import {
  ColXsMdType,
  LocationFilterType,
  SelectOptionType,
  ViewLeadsFilterType,
} from '../utils/Types';
import DatePicker from '../components/KDatePicker';
import { fetchEmployeeNames, taskSelector } from '../features/taskSlice';
import { DEFAULT_OPTION, DEFAULT_OPTION_LOCATION } from '../utils/Constant';
import {
  fetchLeadStatusDrops,
  fetchLeadTypeDrops,
  leadSelector,
} from '../features/leadSlice';
import moment from 'moment';
import LocationFilter from './LocationFilter';
import { useAppDispatch, useAppSelector } from '../store/redux-hooks';

interface IViewLeadsFilterProps {
  onApplyFilters: (viewLeadFilter: ViewLeadsFilterType) => void;
}

const defaultFilter: ViewLeadsFilterType = {
  leadId: '',
  leadType: DEFAULT_OPTION,
  assignedTo: DEFAULT_OPTION,
  status: DEFAULT_OPTION,
  fromDate: undefined,
  toDate: undefined,
  pincode: '',
  zone: DEFAULT_OPTION_LOCATION,
  state: DEFAULT_OPTION_LOCATION,
  district: DEFAULT_OPTION_LOCATION,
  city: DEFAULT_OPTION_LOCATION,
};
const colSize: ColXsMdType = {
  xs: 6,
  md: 3,
};

const ViewLeadsFilter: React.FunctionComponent<IViewLeadsFilterProps> = ({
  onApplyFilters,
}: IViewLeadsFilterProps) => {
  const dispatch = useAppDispatch();

  const optionEmployeeName: SelectOptionType[] =
    useAppSelector(taskSelector).employeeNameOptions;
  const leadTypeOptions: SelectOptionType[] =
    useAppSelector(leadSelector).leadTypeOptions;
  const statusDropOptions: SelectOptionType[] =
    useAppSelector(leadSelector).statusDropOptions;

  const [viewLeadFilter, setViewLeadFilter] =
    useState<ViewLeadsFilterType>(defaultFilter);

  const onResetFilter = () => {
    onApplyFilters(defaultFilter);
    setViewLeadFilter(defaultFilter);
  };

  useEffect(() => {
    dispatch(fetchEmployeeNames());
    dispatch(fetchLeadStatusDrops({ module: 4 }));
    dispatch(fetchLeadTypeDrops({ lobId: 0 }));
  }, []);

  return (
    <div className="border rounded ps-3 pt-2 pb-3 pe-3">
      <Row>
        <Col xs={6} md={3}>
          <SearchBox
            label="Search by Lead Id"
            placeHolder="Search"
            value={viewLeadFilter.leadId}
            onChange={(e: any) => {
              setViewLeadFilter({
                ...viewLeadFilter,
                leadId: e.target.value,
              });
            }}
          />
        </Col>
        <Col xs={6} md={5}>
          <SelectBox
            label="Search by Lead Type"
            value={viewLeadFilter.leadType}
            options={leadTypeOptions}
            onChange={(value: any) => {
              setViewLeadFilter({
                ...viewLeadFilter,
                leadType: value,
              });
            }}
          />
        </Col>
        <Col xs={6} md={3}></Col>
      </Row>

      <Row>
        <Col xs={6} md={3}>
          <SelectBox
            label="Assigned To"
            options={optionEmployeeName}
            className="css-select-bg"
            value={viewLeadFilter.assignedTo}
            onChange={(event: any) => {
              setViewLeadFilter({
                ...viewLeadFilter,
                assignedTo: event,
              });
            }}
          />
        </Col>
        <Col xs={6} md={3}>
          <SelectBox
            label="Status"
            options={statusDropOptions}
            className="css-select-bg"
            value={viewLeadFilter.status}
            onChange={(event: any) => {
              setViewLeadFilter({
                ...viewLeadFilter,
                status: event,
              });
            }}
          />
        </Col>
        <Col xs={6} md={3}>
          <DatePicker
            label="Date From"
            value={viewLeadFilter.fromDate}
            minDate={moment().subtract(1, 'years').toDate()}
            maxDate={new Date()}
            onChange={(date) => {
              let newToDate = viewLeadFilter.toDate;

              const maxPossibleToDate = moment.min(
                moment(date).add(89, 'days'),
                moment()
              );

              // Auto-set toDate to current date if fromDate is today
              if (moment(date).isSame(moment(), 'day')) {
                newToDate = new Date();
              }
              // Reset toDate if existing one is invalid
              else if (
                (newToDate && moment(newToDate).isAfter(maxPossibleToDate)) ||
                moment(newToDate).isBefore(date)
              ) {
                newToDate = undefined;
              }

              setViewLeadFilter({
                ...viewLeadFilter,
                fromDate: date,
                toDate: newToDate,
              });
            }}
          />
        </Col>

        <Col xs={6} md={3}>
          <DatePicker
            label="Date To"
            minDate={viewLeadFilter.fromDate}
            maxDate={
              viewLeadFilter.fromDate
                ? moment
                    .min(
                      moment(viewLeadFilter.fromDate).add(89, 'days'),
                      moment()
                    )
                    .toDate()
                : new Date()
            }
            value={viewLeadFilter.toDate}
            onChange={(date) => {
              setViewLeadFilter({
                ...viewLeadFilter,
                toDate: date,
              });
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <LocationFilter
            size={{
              zone: colSize,
              state: colSize,
              district: colSize,
              city: colSize,
            }}
            filterData={{
              zone: viewLeadFilter.zone,
              state: viewLeadFilter.state,
              district: viewLeadFilter.district,
              city: viewLeadFilter.city,
            }}
            setLocationFilter={(filter: LocationFilterType) => {
              setViewLeadFilter({
                ...viewLeadFilter,
                zone: filter.zone,
                state: filter.state,
                district: filter.district,
                city: filter.city,
              });
            }}
          />
        </Col>
      </Row>
      <div className="d-flex justify-content-left mt-2">
        <Button
          variant="primary"
          className="cl-btn-primary"
          onClick={() => {
            onApplyFilters(viewLeadFilter);
          }}
        >
          Search
        </Button>
        <Button
          variant="outline-primary"
          className="mx-2 cl-btn-outline-primary"
          onClick={() => {
            onResetFilter();
          }}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};

export default ViewLeadsFilter;
